import { XCircle } from '@phosphor-icons/react';
import { SVG_SIZE_L } from 'src/constants';
import classNames from 'classnames';
import { Modal } from 'src/components/Modal';
import { useBreakpoint, useGetUser } from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import { useNotificationModal } from 'src/hooks/useNotificationModal';
import { useGetProductNotificationDataQuery } from 'src/store/services';
import { useUpdateNotificationLastSeenByIdMutation } from 'src/store/services/notificationsApi';
import { LATEST_NOTIFICATION_ID } from 'src/latest_notification';
import styles from './ProductNotificationModal.module.scss';
import { useMemo } from 'react';
import { LazyReactPlayer } from '../LazyReactPlayer';
import { isMobile as isMobileDevice } from 'react-device-detect';
import { jsonBaseUrl } from 'src/store/constants';

export const ProductNotificationModal = () => {
  const { user_id } = useGetUser();

  const { isMobile } = useBreakpoint();

  const { shouldShowNotificationModal, changeShouldShowNotificationModal } =
    useNotificationModal();

  const [updateNotificationLastSeenById] =
    useUpdateNotificationLastSeenByIdMutation();

  const { data } = useGetProductNotificationDataQuery({
    id: LATEST_NOTIFICATION_ID,
  });

  const {
    category,
    title,
    description,
    call_to_action,
    thumbnail_type,
    thumbnail_url,
  } = data || {};

  const label = useMemo(() => {
    switch (category) {
      case 'quick_tip':
        return 'Quick Tip';

      case 'new_feature':
      default:
        return 'New';
    }
  }, [category]);

  const labelClassName = useMemo(() => {
    switch (category) {
      case 'quick_tip':
        return styles.quickTipLabel;
      default:
        return styles.newLabel;
    }
  }, [category]);

  const handleActionClick = () => {
    if (!call_to_action) {
      return;
    }

    window.open(call_to_action.url, '_blank', 'noopener,noreferrer');
  };

  const handleCloseClick = () => {
    changeShouldShowNotificationModal(false);

    updateNotificationLastSeenById({
      user_id,
      last_seen_id: LATEST_NOTIFICATION_ID,
    });
  };

  return (
    <Modal
      open={!!data && shouldShowNotificationModal}
      onClose={handleCloseClick}
      className={styles.root}
      overlayClassName={styles.overlay}
      showCloseIcon={false}
      blockScroll={false}
    >
      <div className={styles.container} data-e2e="product-notification-modal">
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleCloseClick}
          data-e2e="update-modal-close-button"
        >
          <XCircle size={SVG_SIZE_L} weight="fill" />
        </button>

        {thumbnail_url && thumbnail_type === 'video' && (
          <LazyReactPlayer
            className={styles.media}
            url={`${jsonBaseUrl}/notifications/product_notifications/${LATEST_NOTIFICATION_ID}/${thumbnail_url}`}
            height={isMobile ? '192px' : '294px'}
            playing={!isMobileDevice}
            controls
          />
        )}

        {thumbnail_url && thumbnail_type === 'image' && (
          <img
            src={thumbnail_url}
            alt="notification-image"
            className={styles.media}
          />
        )}

        {!thumbnail_url && <div className={styles.media} />}

        <div className={styles.content}>
          <span className={classNames(styles.label, labelClassName)}>
            {label}
          </span>

          {title && <p className={styles.title}>{title}</p>}

          {description && <p className={styles.caption}>{description}</p>}

          <div className={styles.footer}>
            {call_to_action ? (
              <Button color="highlight" onClick={handleActionClick}>
                {call_to_action.text}
              </Button>
            ) : (
              <Button color="highlight" onClick={handleCloseClick}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
